/**
 * Vidu AI Video Generation Constants
 *
 * Vidu via Shengshu Technology.
 *
 * NOTE: Q3 / Q3-Fast are present for tier mapping + future enablement but are
 * disabled in the UI selector until the official api.vidu.com/ent/v2 model slug
 * is verified. See docs/modules/VIDU_Q3_ROUTING.md + project_vidu_q3_routing memory.
 * The backend client raises ViduModelUnavailableError if either is selected.
 *
 * The `priceNs` fields are what Vidu bills US, matching `VIDU_PRICING` in
 * constants/video_generation/vidu.py (credits x VIDU_COST_PER_CREDIT_USD). The
 * `tokensNs` fields are DERIVED from them through `tokenBilling.ts`, the port of
 * `constants/pricing/base.py::cost_usd_to_billed_tokens`.
 *
 * Two bugs lived here until 2026-09: the tokens were literals frozen at a 1.5x
 * markup the backend had already left (so every quote was 25% under the charge),
 * and Q2 -- the only selectable model -- carried $0.28/$0.56 against a real
 * $0.25/$0.45. Vidu does not price Q2 linearly in duration, which is why doubling
 * the 4 s figure for 8 s was wrong.
 */

import { videoCostUsdToTokens } from './tokenBilling';

export const VIDU_MODELS = {
  Q3: 'vidu-q3',
  Q3_FAST: 'vidu-q3-fast',
  Q2: 'vidu-q2',
} as const;

export type ViduModel = (typeof VIDU_MODELS)[keyof typeof VIDU_MODELS];

export const VIDU_DURATIONS = ['4', '8', '16'] as const;
export type ViduDuration = (typeof VIDU_DURATIONS)[number];

export const VIDU_ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4'] as const;
export type ViduAspectRatio = (typeof VIDU_ASPECT_RATIOS)[number];

export const VIDU_STYLES = ['general', 'anime'] as const;
export type ViduStyle = (typeof VIDU_STYLES)[number];

export interface ViduModelOption {
  value: ViduModel;
  label: string;
  description: string;
  /**
   * Vidu's own price to us, per clip length, in USD -- or `null` when this
   * (model, duration) pair has no price because the server will not run it.
   *
   * `null` is deliberately not `0`. A zero here reads as "free" everywhere it is
   * formatted, and until 2026-09 that is exactly what the picker showed for
   * `('16', 'vidu-q2')`: the server answers that pair with
   * `UnpriceableOperationError` ("renders at most 10 s"), while the UI quoted a
   * confident 0 tokens. Absence of a price has to be representable, or the code
   * invents one (RULE #5).
   */
  price4s: number | null;
  price8s: number | null;
  price16s: number | null;
  /** Billed tokens derived from the matching `priceNs`, or `null` when unpriced. */
  tokens4s: number | null;
  tokens8s: number | null;
  tokens16s: number | null;
  badge: string;
  speed: string;
  hasAudio: boolean;
  maxDuration: string;
  /** When true, the option is shown but cannot be selected (e.g. awaiting upstream API). */
  disabled?: boolean;
  /** Tooltip shown when hovering a disabled option. */
  disabledReason?: string;
}

export const VIDU_MODEL_OPTIONS: ViduModelOption[] = [
  {
    value: VIDU_MODELS.Q2,
    label: 'Q2',
    description: 'Flexible duration, high quality',
    price4s: 0.25,
    price8s: 0.45,
    price16s: null,
    tokens4s: videoCostUsdToTokens(0.25),
    tokens8s: videoCostUsdToTokens(0.45),
    tokens16s: null,
    badge: 'DEFAULT',
    speed: '~30s',
    hasAudio: false,
    maxDuration: '8',
  },
  {
    // Every price is null ON PURPOSE. `VideoGenerationPricing.estimate_vidu`
    // answers 'vidu-q3' with ViduModelUnavailableError -- the id is not in the
    // server's catalogue at all (vidu-q1, vidu-q1-classic, vidu-q2, vidu-q2-pro,
    // vidu-q2-pro-fast, vidu-q2-turbo, vidu-v2) -- so there is no price to show.
    // Carrying $0.64/$1.28/$2.56 here, derived or not, dressed an unservable SKU
    // in a credible quote. If Vidu enables Q3, the price arrives with the backend
    // catalogue row, not before it.
    value: VIDU_MODELS.Q3,
    label: 'Q3 (Coming Soon)',
    description: 'Awaiting official Vidu API enablement',
    price4s: null,
    price8s: null,
    price16s: null,
    tokens4s: null,
    tokens8s: null,
    tokens16s: null,
    badge: 'SOON',
    speed: '~60s',
    hasAudio: true,
    maxDuration: '16',
    disabled: true,
    disabledReason: 'Vidu Q3 is not yet available on the official API. Select Q2 in the meantime.',
  },
  {
    // Unpriced for the same reason as Q3 above: ViduModelUnavailableError.
    value: VIDU_MODELS.Q3_FAST,
    label: 'Q3 Fast (Coming Soon)',
    description: 'Awaiting official Vidu API enablement',
    price4s: null,
    price8s: null,
    price16s: null,
    tokens4s: null,
    tokens8s: null,
    tokens16s: null,
    badge: 'SOON',
    speed: '~30s',
    hasAudio: true,
    maxDuration: '8',
    disabled: true,
    disabledReason:
      'Vidu Q3 Fast is not yet available on the official API. Select Q2 in the meantime.',
  },
] as const;

export const VIDU_DURATION_OPTIONS = [
  { value: '4', label: '4 seconds', price: 'base' },
  { value: '8', label: '8 seconds', price: '2x base' },
  { value: '16', label: '16 seconds', price: '4x base (Q3 only)' },
] as const;

export const VIDU_ASPECT_RATIO_OPTIONS = [
  { value: '16:9', label: 'Landscape (16:9)', icon: 'RectangleHorizontal' },
  { value: '9:16', label: 'Portrait (9:16)', icon: 'RectangleVertical' },
  { value: '1:1', label: 'Square (1:1)', icon: 'Square' },
  { value: '4:3', label: 'Classic (4:3)', icon: 'RectangleHorizontal' },
  { value: '3:4', label: 'Portrait Classic (3:4)', icon: 'RectangleVertical' },
] as const;

export const VIDU_STYLE_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'anime', label: 'Anime' },
] as const;

export interface ViduConfig {
  model: ViduModel;
  duration: ViduDuration;
  aspect_ratio: ViduAspectRatio;
  style: ViduStyle;
  enable_audio: boolean;
}

export const DEFAULT_VIDU_CONFIG: ViduConfig = {
  model: 'vidu-q2',
  duration: '8',
  aspect_ratio: '16:9',
  style: 'general',
  enable_audio: false,
};

/** The option row for *model*, or a thrown error naming the unknown id.
 *
 * `ViduModel` is a closed union, so a miss here means VIDU_MODEL_OPTIONS lost a
 * row -- a bug in this file, not bad user input. The previous `if (!option)
 * return 0` turned that bug into a free quote for a job the server bills.
 */
function requireViduOption(model: ViduModel): ViduModelOption {
  const option = VIDU_MODEL_OPTIONS.find((m) => m.value === model);
  if (!option) {
    throw new RangeError(
      `Unknown Vidu model '${model}': it has no row in VIDU_MODEL_OPTIONS, so there ` +
        'is no price for it. Add the row (with the backend catalogue) rather than ' +
        'quoting a number for a model nobody priced.'
    );
  }
  return option;
}

/**
 * Vidu's upstream USD cost for one clip, or `null` when the pair is unpriceable.
 *
 * `null` mirrors the server refusing to quote: `estimate_vidu` raises
 * `UnpriceableOperationError` for ('16', 'vidu-q2') and
 * `ViduModelUnavailableError` for the Q3 ids. Render it as "unavailable" -- do
 * NOT coalesce it to 0, which is what made the picker show a free 16 s Q2 clip.
 */
export function calculateViduCost(duration: ViduDuration, model: ViduModel): number | null {
  const option = requireViduOption(model);

  if (duration === '16') return option.price16s;
  if (duration === '8') return option.price8s;
  return option.price4s;
}

/**
 * Billed tokens for one Vidu clip, or `null` when the pair is unpriceable.
 *
 * See `calculateViduCost` for what `null` means and why it is not 0.
 */
export function calculateViduTokens(duration: ViduDuration, model: ViduModel): number | null {
  const option = requireViduOption(model);

  if (duration === '16') return option.tokens16s;
  if (duration === '8') return option.tokens8s;
  return option.tokens4s;
}

/**
 * Check if a model supports native audio generation
 */
export function supportsViduAudio(model: ViduModel): boolean {
  return model === VIDU_MODELS.Q3 || model === VIDU_MODELS.Q3_FAST;
}

/**
 * Get available durations for a model
 */
export function getViduAvailableDurations(model: ViduModel): ViduDuration[] {
  const maxDuration = VIDU_MODEL_OPTIONS.find((m) => m.value === model)?.maxDuration ?? '8';
  return VIDU_DURATIONS.filter(
    (d) => Number(d) <= Number(maxDuration)
  ) as unknown as ViduDuration[];
}
